// Batch E1.5 — Larger StagePicker for the Deal Detail page sidebar.
// Vertical list with stage label + one-line description + a current marker.
// Shares the network cache with <StageBadge /> via the useStages() hook.

import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useStages } from "./StageBadge";
import { invalidatePipelineCounts } from "@/lib/usePipelineCounts";

export default function StagePicker({ stage, dealId, onChange }) {
  const stages = useStages();
  const [current, setCurrent] = useState(stage);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => setCurrent(stage), [stage]);

  async function applyStage(newStageId) {
    if (newStageId === current || busyId) return;
    const prev = current;
    setCurrent(newStageId); // optimistic
    setBusyId(newStageId);
    try {
      // Phase 36 P0 follow-up #7 — use shared `api` instance so the
      // centralized 401 auto-retry interceptor applies (same race window
      // that previously broke /api/pipeline/board and /api/pipeline/stages
      // on first-load can hit this PATCH too).
      await api.patch(
        `/deals/${dealId}/stage`,
        { stage: newStageId, note: null }
      );
      invalidatePipelineCounts();
      const label = stages.find((s) => s.id === newStageId)?.label || newStageId;
      toast.success(`Stage updated to ${label}`);
      onChange && onChange(newStageId);
    } catch (e) {
      setCurrent(prev);
      toast.error(
        e?.response?.data?.detail || "Could not update stage. Try again."
      );
    } finally {
      setBusyId(null);
    }
  }

  if (!stages.length) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm"
        data-testid="stage-picker-loading"
      >
        Loading pipeline…
      </div>
    );
  }

  return (
    <div
      data-testid={`stage-picker-${dealId}`}
      className="rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
    >
      <div className="border-b border-slate-100 px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Pipeline stage
        </p>
      </div>
      <ul className="py-1">
        {stages.map((s) => {
          const isCurrent = s.id === current;
          const isBusy = busyId === s.id;
          return (
            <li key={s.id}>
              <button
                type="button"
                data-testid={`stage-picker-option-${s.id}`}
                disabled={!!busyId}
                onClick={() => applyStage(s.id)}
                className={`group flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors ${
                  isCurrent
                    ? "bg-slate-50"
                    : "hover:bg-slate-50"
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${s.colors.bg.replace("-100", "-500")}`}
                />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${s.colors.text}`}>
                      {s.label}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
                        Current
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                    {s.description}
                  </span>
                </span>
                {isBusy ? (
                  <Loader2 className="mt-1 h-4 w-4 flex-shrink-0 animate-spin text-slate-400" />
                ) : isCurrent ? (
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-indigo-600" />
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
